import { AbilityBuilder, createMongoAbility, type MongoAbility } from '@casl/ability';
import type { AuthUser } from '@/store/auth';

/**
 * CASL ability layer.
 *
 * Why we have this on top of `useAuthStore().hasAuthority(...)`:
 *  - hasAuthority is a flat string check. Good for "do you have this code?"
 *  - Abilities give us subjects (Student, Fee, Staff…) and verbs (view, edit,
 *    reveal, …) so UI can ask `can('edit', 'Student')` instead of remembering
 *    that "edit a student" means STUDENT_MGMT:EDIT today and maybe something
 *    else tomorrow. Conditions (per-row) come for free if we ever need them.
 *
 * Building the ability is cheap and synchronous — we rebuild from the latest
 * user envelope whenever the auth store changes (see AbilityProvider).
 */

export type Actions =
  | 'view' | 'create' | 'edit' | 'delete' | 'approve'
  | 'reveal'    // unmask a sensitive amount/figure
  | 'mark'      // mark attendance / marks
  | 'manage';   // super-user wildcard

export type Subjects =
  | 'Dashboard'
  | 'FinancialAmount'      // any rupee figure on a tile / report
  | 'Student'
  | 'StudentFees'          // fee tab on student detail
  | 'StudentFinancial'     // full ledger / receipts list for a student
  | 'StudentContact'       // phone, address
  | 'StudentGuardian'      // guardian details
  | 'Attendance'
  | 'Holiday'              // holiday / school calendar management
  | 'Academics'
  | 'Timetable'            // weekly class timetable (mutation)
  | 'Mark'                 // mark entry
  | 'Fee'
  | 'Finance'
  | 'Staff'
  | 'StaffSalary'          // salary/payroll detail on staff profile
  | 'HR'
  | 'Inventory'
  | 'FrontDesk'
  | 'Notification'
  | 'Report'
  | 'Setup'
  | 'User'                 // user-mgmt module
  | 'Role'
  | 'AuditLog'
  | 'all';

export type AppAbility = MongoAbility<[Actions, Subjects]>;

/** Module code → CASL subject. Anything not listed is left out of the rules. */
const MODULE_SUBJECT: Record<string, Subjects> = {
  STUDENT_MGMT: 'Student',
  ATTENDANCE: 'Attendance',
  ACADEMICS: 'Academics',
  FEE_MGMT: 'Fee',
  HR_MGMT: 'Staff',
  INVENTORY: 'Inventory',
  FRONT_DESK: 'FrontDesk',
  FINANCE: 'Finance',
  REPORTS: 'Report',
  SETUP: 'Setup',
  USER_MGMT: 'User',
  NOTIFICATIONS: 'Notification',
};

/** Standard CRUD action → CASL action. */
const ACTION_VERB: Record<string, Actions> = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  APPROVE: 'approve',
  DELETE: 'delete',
};

/**
 * Fine-grained capability codes from V10__role_capabilities.sql.
 * Each maps to an extra (action, subject) rule.
 */
const CAPABILITY_RULES: Record<string, [Actions, Subjects]> = {
  'STUDENT_MGMT:VIEW_FEES':       ['view', 'StudentFees'],
  'STUDENT_MGMT:VIEW_FINANCIAL':  ['view', 'StudentFinancial'],
  'STUDENT_MGMT:VIEW_CONTACT':    ['view', 'StudentContact'],
  'STUDENT_MGMT:VIEW_GUARDIAN':   ['view', 'StudentGuardian'],
  'HR_MGMT:VIEW_SALARY':          ['view', 'StaffSalary'],
  'ATTENDANCE:MARK_ALL_SECTIONS': ['mark', 'Attendance'],
  'ATTENDANCE:MANAGE_HOLIDAYS':   ['manage', 'Holiday'],
  'ACADEMICS:MARK_ALL_SUBJECTS':  ['mark', 'Mark'],
  'ACADEMICS:MANAGE_CONFIG':      ['manage', 'Academics'],
  'ACADEMICS:MANAGE_TIMETABLE':   ['manage', 'Timetable'],
};

/**
 * Roles whose name (from V2 seed) should always see financial amounts and
 * audit logs even when no explicit code is present. Everyone else needs the
 * relevant module code.
 *
 * Audit log is intentionally Super Admin only — Principal was removed in V15
 * (record history forensics belongs with whoever owns the install, not with
 * the school's academic head).
 */
const ROLE_FINANCIAL_REVEAL = new Set([
  // Principal stays here — they see the school-wide dashboard tiles
  // (fees collected today / outstanding dues) and need the eye to work.
  // Their lockdown is at the module level (no Finance / Inventory / payroll
  // register) via V15, not at the reveal toggle.
  'Super Admin', 'Principal', 'Accountant', 'Cashier',
]);
const ROLE_AUDIT_VIEW = new Set([
  'Super Admin',
]);

export function buildAbility(user: AuthUser | null): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  if (!user) return build();

  // Super Admin gets the wildcard. The matrix probably grants everything
  // anyway, but rolling it up shaves a few dozen rules per session.
  if (user.role === 'Super Admin') {
    can('manage', 'all');
    return build();
  }

  // Walk permissions[] and turn each MODULE:ACTION code into a rule.
  for (const code of user.permissions ?? []) {
    const [module, action] = code.split(':');
    if (!module || !action) continue;

    const cap = CAPABILITY_RULES[code];
    if (cap) {
      can(cap[0], cap[1]);
      continue;
    }

    const subject = MODULE_SUBJECT[module];
    const verb = ACTION_VERB[action];
    if (subject && verb) can(verb, subject);
  }

  // Dashboard is visible to anyone authenticated.
  can('view', 'Dashboard');

  // Financial amount unmasking — keyed off role, not module access. Cashier
  // sees fee figures but not full ledger; their dashboard reveal is still OK
  // because tiles only show what the BFF already returned to their role.
  if (ROLE_FINANCIAL_REVEAL.has(user.role)) {
    can('reveal', 'FinancialAmount');
  }

  if (ROLE_AUDIT_VIEW.has(user.role)) {
    can('view', 'AuditLog');
  }

  return build();
}
